import { useState } from 'react'
import styles from './DISCBarChart.module.scss'
import { DiscType } from '@crystal-eyes/types'
import { archetypes } from '@crystal-eyes/disc'
import { circumplexToFourPercentage } from '@crystal-eyes/lib/personality_math'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'

const FONT_SIZE = 60
const W = 800
const DEFAULT_HEIGHT = 600
const BAR_WIDTH = 80
const BAR_MARGIN = 115
const PADDING_BOTTOM = FONT_SIZE * 2 + 30
const PADDING_TOP = 30
const PADDING_LEFT = 70
const MAX_BAR_HEIGHT = DEFAULT_HEIGHT - PADDING_BOTTOM - PADDING_TOP
const BORDER_RADIUS = 10

interface Identity {
  id: string
  personalInfo?: {fullName?: string}
  snapshot?: { personality?: {id?: string, discType?: DiscType}}
}

type Props = {
  className?: string
  style?: React.CSSProperties
  identities: Identity[]
}

type Hoverable = { identity?: Identity; discType?: DiscType } | null

export default function DISCBarChart({
  identities,
  className = '',
  style,
}: Props) {
  const [hoveringOver, setHoveringOver] = useState<Hoverable>(null)
  const { data: personality1 } = usePersonality({ identity: identities[0] })
  const { data: personality2 } = usePersonality({ identity: identities[1] })

  const { data: personalInfo1 } = usePersonalInfo({ identity: identities[0] })
  const { data: personalInfo2 } = usePersonalInfo({ identity: identities[1] })

  return (
    <div
      className={`${styles.discBarChart} ${className} ${
        identities.length > 1 ? styles.double : styles.single
      }`}
      style={{ fontSize: FONT_SIZE, ...(style || {}) }}
    >
      {(identities.length === 1 || !identities.length) && (
        <div className="charts">
          <HoverInfo hovering={hoveringOver} />

          <div className="chart">
            <BarChart
              identity={identities[0]}
              setHoveringOver={setHoveringOver}
            />
          </div>
        </div>
      )}

      {identities.length > 1 && (
        <div className="charts">
          <HoverInfo hovering={hoveringOver} />

          <div className="chart">
            <h4 className={`CE disc-color ${personality1?.discType}`}>
              {personalInfo1?.fullName?.split(' ')[0]}
            </h4>
            <BarChart
              identity={identities[0]}
              setHoveringOver={setHoveringOver}
            />
          </div>

          <div className="chart">
            <h4 className={`CE disc-color ${personality2?.discType}`}>
              {personalInfo2?.fullName?.split(' ')[0]}
            </h4>
            <BarChart
              identity={identities[1]}
              setHoveringOver={setHoveringOver}
            />
          </div>
        </div>
      )}
    </div>
  )
}

type ChartProps = {
  identity: Identity
  setHoveringOver: (hovering: Hoverable) => void
}
function BarChart({ identity, setHoveringOver }: ChartProps) {
  const { data: personality } = usePersonality({ identity })

  const discType = personality?.discType
  const circumplex = personality?.discCircumplex
  const useEmptyState = !identity || !personality?.discType
  const H = useEmptyState ? 400 : DEFAULT_HEIGHT

  const discValues =
    discType && circumplex
      ? circumplexToFourPercentage(
          discType,
          circumplex?.degrees,
          circumplex?.intensity,
        )
      : { D: 0.0, I: 0.0, S: 0.0, C: 0.0 }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="bar-chart"
      onMouseOut={() => setHoveringOver(null)}
    >
      {Object.keys(discValues).map((discType, i) => {
        const percentage = discValues[discType] as number
        const barHeight = useEmptyState ? 15 : MAX_BAR_HEIGHT * percentage

        return (
          <g
            className={`CE fill-disc ${discType}`}
            transform={`translate(${
              PADDING_LEFT + (BAR_WIDTH + BAR_MARGIN) * i
            }, 0)`}
            key={discType}
          >
            <rect
              width={BAR_WIDTH}
              height={barHeight}
              rx={BORDER_RADIUS}
              y={H - PADDING_BOTTOM - MAX_BAR_HEIGHT * percentage}
              onMouseOver={() =>
                setHoveringOver({ discType: discType as DiscType })
              }
            />

            <text
              x={BAR_WIDTH / 2}
              y={H - PADDING_BOTTOM / 2}
              textAnchor="middle"
              className="disc-type"
            >
              {discType}
            </text>

            {!useEmptyState && (
              <text
                x={BAR_WIDTH / 2}
                y={H - PADDING_BOTTOM / 2 + FONT_SIZE}
                textAnchor="middle"
                style={{ fontSize: FONT_SIZE * 0.8 }}
                className="percentage"
              >
                {Math.floor(percentage * 100)}%
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

const HOVER_TITLE_FONT_SIZE = 18
const HOVER_SUBTITLE_FONT_SIZE = 16

function HoverInfo({ hovering }: { hovering: Hoverable }) {
  const discType =
    hovering?.discType || hovering?.identity?.snapshot?.personality?.discType
  if (!discType) return null

  const archetype = archetypes[discType]

  const title = hovering.identity?.personalInfo?.fullName || archetype.title
  const descriptors = [...archetype.descriptors]
  const lastDescriptor = descriptors.pop()
  const subtitle = descriptors.join(', ') + ' and ' + lastDescriptor

  return (
    <div className="hover-info">
      <svg viewBox="0 0 300 80">
        <rect className="background" width="300" height="80" />

        <rect
          className={`CE fill-disc ${discType} sidebar`}
          width="10"
          height="85"
        />

        <text
          textAnchor="start"
          x="20"
          y="25"
          className={`CE fill-disc ${discType} title`}
          style={{ fontSize: HOVER_TITLE_FONT_SIZE }}
        >
          {title} ({discType})
        </text>

        <switch>
          <foreignObject
            x="20"
            y={HOVER_TITLE_FONT_SIZE + 10}
            width="275"
            height="80"
            className="subtitle"
            style={{ fontSize: HOVER_SUBTITLE_FONT_SIZE }}
          >
            <p className="subtitle">{subtitle}</p>
          </foreignObject>

          <text textAnchor="start" className={`subtitle`}>
            {subtitle}
          </text>
        </switch>
      </svg>
    </div>
  )
}
